// ─── Action Registry ──────────────────────────────────────────────────────────
//
// Stores ActionDefinition from the Phase 3 canonical schema (actionSchema.ts).
// Registration-time validation enforces:
//   L2C-P1: state key format (Approach A - Registration-Time Rejection)
//   AHG-3:  required fields present per action type
//
// Validation is batch-collecting: all violations are gathered before throwing
// a single ActionSchemaValidationError.

import { compileExpression, ExpressionCompileError } from '@/core/expressionCompiler';
import type {
  ActionDefinition,
  SetStateAction,
  ToggleStateAction,
  FilterCollectionAction,
  ConditionalAction,
  GuardAction,
  SwitchCaseAction,
  TriggerDefinition,
} from '@/core/schema/actionSchema';

export type { ActionDefinition };

// ─── Validation Error ─────────────────────────────────────────────────────────

export interface ActionSchemaViolation {
  actionId: string;
  field: string;
  message: string;
}

export class ActionSchemaValidationError extends Error {
  constructor(public readonly violations: ActionSchemaViolation[]) {
    super(
      `Action schema validation failed with ${violations.length} violation(s):\n` +
      violations.map(v => `  [${v.actionId}] ${v.field}: ${v.message}`).join('\n')
    );
    this.name = 'ActionSchemaValidationError';
  }
}

// ─── State Key Validation (L2C-P1) ───────────────────────────────────────────

// Valid formats: "global:{key}", "page:{ownerId}:{key}", "component:{ownerId}:{key}"
const STATE_KEY_REGEX = /^(global:[^:]+|page:[^:]+:[^:]+|component:[^:]+:[^:]+)$/;

export function isValidStateKey(key: string): boolean {
  return STATE_KEY_REGEX.test(key);
}

// ─── Registry ─────────────────────────────────────────────────────────────────

class ActionRegistryImpl {
  private actions = new Map<string, ActionDefinition>();

  /**
   * Register a batch of ActionDefinitions.
   * Validates all definitions before registering any.
   * Throws ActionSchemaValidationError if any violations are found.
   */
  register(definitions: ActionDefinition[]): void {
    const violations: ActionSchemaViolation[] = [];

    for (const def of definitions) {
      violations.push(...validateDefinition(def));
    }

    if (violations.length > 0) {
      throw new ActionSchemaValidationError(violations);
    }

    for (const def of definitions) {
      this.actions.set(def.id, def);
    }
  }

  get(id: string): ActionDefinition | undefined {
    return this.actions.get(id);
  }

  has(id: string): boolean {
    return this.actions.has(id);
  }

  all(): ActionDefinition[] {
    return [...this.actions.values()];
  }

  clear(): void {
    this.actions.clear();
  }
}

export const actionRegistry = new ActionRegistryImpl();

// ─── Validation Logic ─────────────────────────────────────────────────────────

function validateDefinition(def: ActionDefinition): ActionSchemaViolation[] {
  const v: ActionSchemaViolation[] = [];

  switch (def.type) {
    case 'set_state': {
      const d = def as SetStateAction;
      if (!isValidStateKey(d.key)) {
        v.push({ actionId: def.id, field: 'key', message: `"${d.key}" is not a valid state key. Expected format: global:{key}, page:{ownerId}:{key}, or component:{ownerId}:{key}` });
      }
      break;
    }
    case 'toggle_state': {
      const d = def as ToggleStateAction;
      if (!isValidStateKey(d.key)) {
        v.push({ actionId: def.id, field: 'key', message: `"${d.key}" is not a valid state key. Expected format: global:{key}, page:{ownerId}:{key}, or component:{ownerId}:{key}` });
      }
      break;
    }
    case 'conditional': {
      const d = def as ConditionalAction;
      try {
        compileExpression(d.condition);
      } catch (e) {
        const msg = e instanceof ExpressionCompileError ? e.message : String(e);
        v.push({ actionId: def.id, field: 'condition', message: `Expression compile error: ${msg}` });
      }
      break;
    }
    case 'guard': {
      const d = def as GuardAction;
      try {
        compileExpression(d.condition);
      } catch (e) {
        const msg = e instanceof ExpressionCompileError ? e.message : String(e);
        v.push({ actionId: def.id, field: 'condition', message: `Expression compile error: ${msg}` });
      }
      break;
    }
    case 'switch_case': {
      const d = def as SwitchCaseAction;
      try {
        compileExpression(d.expression);
      } catch (e) {
        const msg = e instanceof ExpressionCompileError ? e.message : String(e);
        v.push({ actionId: def.id, field: 'expression', message: `Expression compile error: ${msg}` });
      }
      break;
    }
    case 'filter_collection': {
      const d = def as FilterCollectionAction;
      if (!isValidStateKey(d.stateKey)) {
        v.push({ actionId: def.id, field: 'stateKey', message: `"${d.stateKey}" is not a valid state key. Expected format: global:{key}, page:{ownerId}:{key}, or component:{ownerId}:{key}` });
      }
      try {
        compileExpression(d.condition);
      } catch (e) {
        const msg = e instanceof ExpressionCompileError ? e.message : String(e);
        v.push({ actionId: def.id, field: 'condition', message: `Expression compile error: ${msg}` });
      }
      break;
    }
  }

  return v;
}

// ─── Trigger Validation ───────────────────────────────────────────────────────

export function validateTriggerCondition(trigger: TriggerDefinition): void {
  if (!trigger.condition) return;
  try {
    compileExpression(trigger.condition);
  } catch (e) {
    const msg = e instanceof ExpressionCompileError ? e.message : String(e);
    throw new ActionSchemaValidationError([
      { actionId: `trigger:${trigger.id}`, field: 'condition', message: `Expression compile error: ${msg}` }
    ]);
  }
}
